(() => {
  if (window.CurlKitsShop) {
    window.CurlKitsShop.mount(document);
    return;
  }

  const instances = new Map();
  const selector = '[data-curl-kits-shop]';
  let activeInstance = null;

  const initialize = (root) => {
    if (instances.has(root)) return;

    const controller = new AbortController();
    const overlay = root.querySelector('[data-modal-overlay]');
    const waitlistModal = root.querySelector('[data-waitlist-modal]');
    const variantModal = root.querySelector('[data-variant-modal]');
    const waitlistForm = root.querySelector('[data-waitlist-form]');
    const waitlistHeading = root.querySelector('[data-waitlist-heading]');
    const waitlistStatus = root.querySelector('[data-waitlist-status]');
    const variantHeading = root.querySelector('[data-variant-heading]');
    const variantPrice = root.querySelector('[data-variant-price]');
    const variantOptions = root.querySelector('[data-variant-options]');
    const variantStatus = root.querySelector('[data-variant-status]');
    let activeModal = null;
    let returnFocus = null;
    let frame = null;
    let productData = null;
    let waitlistRequest = null;
    let waitlistVersion = 0;

    const setStatus = (element, message = '', state = '') => {
      if (!element) return;
      element.textContent = message;
      element.hidden = !message;
      if (state) element.dataset.state = state;
      else delete element.dataset.state;
    };

    const setHeadingHtml = (element, html = '') => {
      if (!element) return '';
      element.innerHTML = html;
      const copy = element.cloneNode(true);
      copy.querySelectorAll('br').forEach((lineBreak) => lineBreak.replaceWith(' '));
      return copy.textContent.replace(/\s+/g, ' ').trim();
    };

    const setModalVisibility = (modal, open) => {
      if (!modal) return;
      modal.classList.toggle('is-open', open);
      modal.setAttribute('aria-hidden', String(!open));
      modal.inert = !open;
    };

    const focusableElements = () => activeModal ? [...activeModal.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.closest('[hidden], [aria-hidden="true"]') && element.getClientRects().length) : [];

    const handleKeydown = (event) => {
      if (!activeModal) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableElements();
      if (!focusable.length) {
        event.preventDefault();
        activeModal.focus({ preventScroll: true });
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !activeModal.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !activeModal.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleFocus = (event) => {
      if (!activeModal || activeModal.contains(event.target)) return;
      (focusableElements()[0] || activeModal).focus({ preventScroll: true });
    };

    const closeModal = (restoreFocus = true) => {
      if (!activeModal) return;
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
      setModalVisibility(activeModal, false);
      overlay?.classList.remove('is-open');
      overlay?.setAttribute('aria-hidden', 'true');
      activeModal = null;
      document.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('focusin', handleFocus);
      if (activeInstance === instance) activeInstance = null;
      if (!document.querySelector('.waitlist-modal.is-open')) document.body.classList.remove('waitlist-modal-open');
      if (restoreFocus && returnFocus instanceof HTMLElement && returnFocus.isConnected) {
        returnFocus.focus({ preventScroll: true });
      }
      returnFocus = null;
    };

    const openModal = (modal, trigger = null) => {
      if (!modal || !overlay) return;
      if (activeInstance && activeInstance !== instance) activeInstance.close(false);
      if (!activeModal) returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
      if (activeModal && activeModal !== modal) setModalVisibility(activeModal, false);
      activeModal = modal;
      activeInstance = instance;
      setModalVisibility(modal, true);
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('waitlist-modal-open');
      document.addEventListener('keydown', handleKeydown);
      document.addEventListener('focusin', handleFocus);
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = null;
        if (activeModal === modal) (focusableElements()[0] || modal).focus({ preventScroll: true });
      });
    };

    const openWaitlist = ({ title, productId, variantId }, trigger = null) => {
      if (!waitlistModal || !waitlistForm || !waitlistHeading) return;
      waitlistRequest?.abort();
      waitlistVersion += 1;
      waitlistModal.dataset.productId = String(productId || '');
      waitlistModal.dataset.variantId = String(variantId || '');
      const accessibleTitle = setHeadingHtml(waitlistHeading, title || '');
      waitlistModal.setAttribute('aria-label', accessibleTitle ? `${root.dataset.waitlistLabel}: ${accessibleTitle}` : root.dataset.waitlistLabel);
      waitlistForm.querySelectorAll('input, button[type="submit"]').forEach((element) => {
        element.disabled = false;
        element.classList.remove('is-loading');
        element.removeAttribute('aria-busy');
      });
      waitlistForm.reset();
      setStatus(waitlistStatus);
      openModal(waitlistModal, trigger);
    };

    const renderVariantOptions = () => {
      if (!variantOptions || !productData) return;
      variantOptions.replaceChildren();
      setStatus(variantStatus);
      const displayedVariant = productData.variants.find((variant) => variant.available) || productData.variants[0];
      if (variantPrice) variantPrice.textContent = displayedVariant?.price || '';
      productData.variants.forEach((variant) => {
        const link = document.createElement('a');
        const name = document.createElement('span');
        const action = document.createElement('strong');
        link.href = '#';
        if (!variant.available) link.classList.add('is-sold-out');
        link.dataset.variantChoice = String(variant.id);
        link.dataset.variantAvailable = String(Boolean(variant.available));
        name.textContent = variant.title;
        action.textContent = `${variant.available ? root.dataset.selectLabel : root.dataset.waitlistLabel} ↗︎`;
        link.append(name, action);
        variantOptions.append(link);
      });
    };

    const openVariantModal = (trigger) => {
      const card = trigger.closest('.kits-card');
      const json = card?.querySelector('[data-product-variants]');
      if (!card || !json || !variantModal || !variantHeading) return;
      try {
        productData = JSON.parse(json.textContent);
        if (!Array.isArray(productData.variants) || !productData.variants.length) throw new Error(root.dataset.addError);
        variantModal.dataset.productId = String(productData.id);
        variantModal.dataset.productTitle = trigger.dataset.title || productData.title;
        variantModal.dataset.productHandle = trigger.dataset.productHandle || productData.handle;
        setHeadingHtml(variantHeading, variantModal.dataset.productTitle);
        renderVariantOptions();
        openModal(variantModal, trigger);
      } catch (error) {
        setStatus(card.querySelector('[data-card-status]'), root.dataset.addError, 'error');
      }
    };

    const addVariantToCart = async (variantId, button, statusElement) => {
      if (!variantId || !button || button.getAttribute('aria-busy') === 'true') return null;
      const canDisable = 'disabled' in button;
      const wasDisabled = canDisable ? button.disabled : false;
      if (canDisable) button.disabled = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      setStatus(statusElement);
      try {
        const shopRoot = window.Shopify?.routes?.root || '/';
        const endpoint = new URL(`${shopRoot}cart/add.js`, window.location.origin);
        const response = await fetch(endpoint.toString(), {
          method: 'POST',
          credentials: 'same-origin',
          signal: controller.signal,
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || root.dataset.addError);
        if (activeModal === variantModal) closeModal(false);
        document.dispatchEvent(new CustomEvent('product:added', {
          detail: { variantId: Number(variantId), item: result }
        }));
        return result;
      } catch (error) {
        if (error.name !== 'AbortError') setStatus(statusElement, error.message || root.dataset.addError, 'error');
        return null;
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        if (canDisable) button.disabled = wasDisabled;
      }
    };

    const submitWaitlist = async () => {
      if (!waitlistForm || !waitlistModal || !waitlistForm.reportValidity()) return;
      const button = waitlistForm.querySelector('[type="submit"]');
      if (!button || button.disabled) return;
      const formData = new FormData(waitlistForm);
      const email = String(formData.get('email') || '').trim();
      const phone = String(formData.get('phone') || '').trim().replace(/[\s().-]/g, '');
      const variantId = Number(waitlistModal.dataset.variantId);
      const companyId = root.dataset.klaviyoCompanyId;
      if (!email || !variantId || !companyId) {
        setStatus(waitlistStatus, root.dataset.waitlistError, 'error');
        return;
      }
      const profileAttributes = { email };
      const channels = ['EMAIL'];
      if (phone) {
        profileAttributes.phone_number = phone;
        channels.push('SMS');
      }
      const version = waitlistVersion;
      waitlistRequest?.abort();
      waitlistRequest = new AbortController();
      button.disabled = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      setStatus(waitlistStatus);
      try {
        const endpoint = new URL('https://a.klaviyo.com/client/back-in-stock-subscriptions');
        endpoint.searchParams.set('company_id', companyId);
        const response = await fetch(endpoint.toString(), {
          method: 'POST',
          signal: waitlistRequest.signal,
          headers: {
            'Accept': 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            'revision': '2026-07-15'
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels,
                profile: { data: { type: 'profile', attributes: profileAttributes } }
              },
              relationships: {
                variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } }
              }
            }
          })
        });
        if (response.status !== 202) {
          const result = await response.json().catch(() => ({}));
          throw new Error(result.errors?.[0]?.detail || root.dataset.waitlistError);
        }
        if (version !== waitlistVersion || controller.signal.aborted) return;
        setStatus(waitlistStatus, root.dataset.waitlistSuccess, 'success');
        waitlistForm.querySelectorAll('input').forEach((input) => { input.disabled = true; });
      } catch (error) {
        if (error.name !== 'AbortError' && version === waitlistVersion && !controller.signal.aborted) {
          setStatus(waitlistStatus, error.message || root.dataset.waitlistError, 'error');
          button.disabled = false;
        }
      } finally {
        if (version === waitlistVersion) {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
        }
      }
    };

    root.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('[data-modal-overlay], [data-modal-close]')) {
        event.preventDefault();
        closeModal();
        return;
      }
      const quickAdd = target.closest('[data-quick-add]');
      if (quickAdd) {
        event.preventDefault();
        addVariantToCart(quickAdd.dataset.variantId, quickAdd, quickAdd.closest('.kits-card')?.querySelector('[data-card-status]'));
        return;
      }
      const waitlistTrigger = target.closest('[data-waitlist-open]');
      if (waitlistTrigger) {
        event.preventDefault();
        openWaitlist({
          title: waitlistTrigger.dataset.title,
          productId: waitlistTrigger.dataset.productId,
          variantId: waitlistTrigger.dataset.variantId
        }, waitlistTrigger);
        return;
      }
      const variantTrigger = target.closest('[data-variant-open]');
      if (variantTrigger) {
        event.preventDefault();
        openVariantModal(variantTrigger);
        return;
      }
      const choice = target.closest('[data-variant-choice]');
      if (!choice || !productData) return;
      event.preventDefault();
      const variant = productData.variants.find((item) => String(item.id) === choice.dataset.variantChoice);
      if (!variant) return;
      if (!variant.available) {
        openWaitlist({
          title: variantModal.dataset.productTitle,
          productId: variantModal.dataset.productId,
          variantId: variant.id
        });
      } else {
        addVariantToCart(variant.id, choice, variantStatus);
      }
    }, { signal: controller.signal });

    root.addEventListener('submit', (event) => {
      if (event.target !== waitlistForm) return;
      event.preventDefault();
      submitWaitlist();
    }, { signal: controller.signal });

    const instance = {
      close: closeModal,
      destroy() {
        closeModal();
        controller.abort();
        waitlistRequest?.abort();
        instances.delete(root);
      }
    };
    instances.set(root, instance);
    setModalVisibility(waitlistModal, false);
    setModalVisibility(variantModal, false);

    const query = new URLSearchParams(window.location.search);
    const waitlistHandle = query.get('waitlist');
    if (waitlistHandle) {
      const card = [...root.querySelectorAll('.kits-card[data-product-handle]')]
        .find((element) => element.dataset.productHandle === waitlistHandle);
      if (card) openWaitlist({
        title: card.dataset.productTitle,
        productId: card.dataset.productId,
        variantId: card.dataset.defaultVariantId
      });
    } else {
      const trigger = [...root.querySelectorAll('[data-variant-open]')].find((element) => {
        const key = element.dataset.variantQueryKey;
        return key && element.dataset.productHandle && query.get(key) === element.dataset.productHandle;
      });
      if (trigger) openVariantModal(trigger);
    }
  };

  const mount = (scope = document) => {
    if (scope.matches?.(selector)) initialize(scope);
    scope.querySelectorAll(selector).forEach(initialize);
  };

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance, root) => {
      if (event.target === root || event.target.contains(root)) instance.destroy();
    });
  });
  window.CurlKitsShop = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();
})();
